import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { api, type CustomerListItem, type ListCustomersResponse } from '@/lib/api';
import { phoneCountry, formatPhoneDisplay } from '@/lib/phone-country';
import { fmtCompactUsd } from '@/lib/format';

interface Props {
  selectedCustomerId: string | null;
}

export function CustomerList({ selectedCustomerId }: Props) {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customers', { q }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const qs = params.toString();
      return api<ListCustomersResponse>(`/api/customers${qs ? '?' + qs : ''}`, {
        tokenGetter: () => getToken(),
      });
    },
    staleTime: 10_000,
  });

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-border bg-background flex-shrink-0">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, domain, contact, PN, RFQ #, PO #…"
          className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
        />
      </div>

      {isLoading && <div className="p-4 text-sm text-foreground-muted">Loading…</div>}
      {isError && (
        <div className="p-4 text-sm text-danger">
          {(error as { message?: string })?.message ?? 'Failed to load customers'}
        </div>
      )}

      <ul className="flex-1 overflow-auto">
        {(data?.customers ?? []).map((c) => (
          <CustomerRow key={c.id} c={c} selected={c.id === selectedCustomerId} />
        ))}
        {data && data.customers.length === 0 && (
          <li className="p-4 text-sm italic text-foreground-muted">
            {q ? 'No matches.' : 'No customers yet. They populate as RFQs come in.'}
          </li>
        )}
      </ul>
    </div>
  );
}

function CustomerRow({ c, selected }: { c: CustomerListItem; selected: boolean }) {
  // Derive country/flag from contact phone country code; falls back to
  // customer.countryCode (ISO-2) when no phone is on file.
  const phoneInfo = phoneCountry(c.contactPhone);
  return (
    <li>
      <Link
        href={`/customers/${c.id}`}
        className={`block px-4 py-3 border-b border-border hover:bg-surface-muted transition-colors ${
          selected ? 'bg-surface-muted/80' : ''
        }`}
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="font-semibold text-sm flex items-center gap-2 min-w-0">
            <span className="truncate">{c.displayName ?? c.legalName}</span>
            {c.hasAog && <span className="pill pill-warning text-[10px]">AOG×{c.aogCount}</span>}
            {c.isFaaPart145 && <span className="pill pill-info text-[10px]">FAA-145</span>}
            {c.isMilitary && <span className="pill pill-default text-[10px]">MIL</span>}
          </div>
          <div className="font-mono text-sm font-semibold text-success whitespace-nowrap">
            {fmtCompactUsd(c.lifetimePaidCents)}
          </div>
        </div>
        {(c.contactFullName || c.contactPhone || c.contactEmail) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground">
            {c.contactFullName && <span className="font-medium">{c.contactFullName}</span>}
            {c.contactPhone && (
              <span className="font-mono text-foreground-muted">
                {formatPhoneDisplay(c.contactPhone)}
              </span>
            )}
            {!c.contactFullName && !c.contactPhone && c.contactEmail && (
              <span className="text-foreground-muted">{c.contactEmail}</span>
            )}
            {phoneInfo && (
              <span
                className="text-foreground-muted"
                title={phoneInfo.name}
                aria-label={phoneInfo.name}
              >
                {phoneInfo.flag} {phoneInfo.iso2}
              </span>
            )}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-foreground-muted">
          <span>{c.lifetimeRfqs} RFQ{c.lifetimeRfqs === 1 ? '' : 's'}</span>
          {c.rfqs90d > 0 && <span>· {c.rfqs90d} in 90d</span>}
          {c.emailDomain && <span>· {c.emailDomain}</span>}
          {!phoneInfo && c.countryCode && <span>· {c.countryCode}</span>}
          {c.lastRfqAt && (
            <span>· last RFQ {new Date(c.lastRfqAt).toLocaleDateString()}</span>
          )}
        </div>
      </Link>
    </li>
  );
}
